import { 
  Type, 
  FileText, 
  Hash, 
  SlidersHorizontal, 
  List, 
  Image, 
  ToggleLeft, 
  Upload, 
  Mail, 
  Lock, 
  Link, 
  Phone,
  Layers,
  Calendar,
  Clock
} from 'lucide-react';

// Form Builder Type Definitions

export interface FormOption {
  label: string;
  value: string;
}

export interface ImageOption {
  label: string;
  value: string;
  imageUrl: string;
  description?: string;
}

export interface PromptBuilderConfig {
  template: string; // Template string with placeholders like "Generate a {subject} in {style} style."
  sub_fields: FormField[]; // Array of nested form fields
}

export interface FormField {
  id: string;
  key?: string; // Add key property for API parameter mapping
  type: string;
  label: string;
  variable_name: string;
  additional_variables?: string[];
  additional_variable_types?: { [key: string]: 'string' | 'list' };
  required: boolean;
  placeholder?: string;
  description?: string;
  advanced?: boolean;
  defaultValue?: any;
  hidden?: boolean;
  min?: number;
  max?: number;
  step?: number;
  rows?: number;
  options?: FormOption[];
  multiple?: boolean;
  fileLimit?: number; // Limit for multiple file selection
  accept?: string;
  imageOptions?: ImageOption[];
  allowCustomValue?: boolean;
  customValueLabel?: string;
  customButtonLabel?: string;
  customInputPlaceholder?: string;
  imageValidationPrompt?: string;
  // Prompt enhancement settings (for textarea fields)
  enablePromptEnhancement?: boolean;
  systemInstructions?: string;
  // Prompt builder configuration (for prompt_builder fields)
  promptBuilderConfig?: PromptBuilderConfig;
}

export interface FormSection {
  id: string;
  title: string;
  fields: FormField[];
}

export interface FormBuilderState {
  sections: FormSection[];
  selectedField: FormField | null;
  isPreviewOpen: boolean;
}

// Available field types
export const FIELD_TYPES = [
  { value: 'text', label: 'Text Input', icon: Type },
  { value: 'textarea', label: 'Text Area', icon: FileText },
  { value: 'number', label: 'Number Input', icon: Hash },
  { value: 'range', label: 'Range Slider', icon: SlidersHorizontal },
  { value: 'select', label: 'Dropdown', icon: List },
  { value: 'dropdown_with_image', label: 'Dropdown with Images', icon: Image },
  { value: 'boolean', label: 'Toggle Switch', icon: ToggleLeft },
  { value: 'file', label: 'File Upload', icon: Upload },
  { value: 'prompt_builder', label: 'Prompt Builder', icon: Layers },
  { value: 'email', label: 'Email Input', icon: Mail },
  { value: 'password', label: 'Password Input', icon: Lock },
  { value: 'url', label: 'URL Input', icon: Link },
  { value: 'tel', label: 'Phone Input', icon: Phone },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'time', label: 'Time', icon: Clock }
] as const;

// Field type categories for organization
export const FIELD_CATEGORIES = {
  basic: ['text', 'textarea', 'number'],
  selection: ['select', 'dropdown_with_image', 'boolean'],
  advanced: ['range', 'file', 'prompt_builder'],
  specialized: ['email', 'password', 'url', 'tel', 'date', 'time']
} as const;
// Pro form configuration interface
export interface ProFormConfig {
  formName: string;
  formDescription?: string;
  routePath: string;
  requestType: string;
  appId: string;
  creditCost: number;
  exampleMediaUrl?: string;
  exampleMediaType?: 'image' | 'video' | 'audio';
}